import { Scene } from '@babylonjs/core/scene';
import { eEasing } from '@kb3d/models/classes/enums';
import { Kb3dObject } from '@kb3d/models/classes/kb3d-object';
import { KbColor } from '@kb3d/models/classes/primitives/kb-color';
import { KbQuaternion } from '@kb3d/models/classes/primitives/kb-quaternion';
import { KbVector } from '@kb3d/models/classes/primitives/kb-vector';
import { Token } from '@kb3d/models/classes/token';
import TWEEN from '@tweenjs/tween.js';
import { KbViewer } from '../kb-viewer';

export interface IAnimation {
    name?: string;
    /** the node to animate */
    node: Kb3dObject;
    /** the property of the node that will be animated */
    prop: string;
    /** the $type of the property value */
    propType?: string;
    /** the value to animate to */
    toValue: any;
    easing?: eEasing;
    /** the length of the animation */
    duration?: number;
    /** the number of iterations to run the animations.  -1 for infinite */
    iterations?: number;
    /** the number of ms to wait before starting the animation */
    delay?: number;
}

export interface IActiveAnimation {
    animation: IAnimation;
    tween: TWEEN.Tween;
}

export class AnimationModule {
    constructor(private kbViewer: KbViewer, private scene: Scene) {}

    protected activeAnimations = new Set<IActiveAnimation>();

    public get animationsAreRunning() {
        return this.activeAnimations.size > 0;
    }

    public queue(a: IAnimation): Promise<void> {
        if (!a.node) throw 'node required';
        if (a.toValue == null) throw 'value required';

        a = this.applyDefaults(a);

        return new Promise(resolve => {
            const origValue = (a.node as any)[a.prop];
            const origValueType = origValue?.$type;
            const val = this.valueIn(origValue);
            const toVal = this.valueIn(a.toValue);

            const tween = new TWEEN.Tween(val)
                .to(toVal, a.duration!)
                .delay(a.delay!)
                .repeat(a.iterations! < 0 ? Infinity : a.iterations! - 1)
                .easing(this.getTweenEasingFn(a.easing!))
                .onUpdate(() => {
                    (a.node as any)[a.prop] = this.valueOut(val, origValueType);
                })
                .onComplete(() => {
                    this.activeAnimations.delete(activeAnim);
                    resolve();
                })
                .start();

            const activeAnim = { animation: a, tween };
            this.activeAnimations.add(activeAnim);
            this.kbViewer.animationWarningsNeedsUpdate.next(this.activeAnimations.size);
        });
    }

    public stop(name: string) {
        if (name != null) {
            const anims = [...this.activeAnimations.values()];
            for (const t of anims.filter(a => name.isEqual(a.animation.name))) {
                t.tween.stop();
                this.activeAnimations.delete(t);
            }
        }
    }

    public stopAll() {
        for (const a of this.activeAnimations) {
            a.tween.stop();
        }
        this.activeAnimations.clear();
    }

    /** should be called by the render loop */
    public update() {
        TWEEN.update(); //update all animations
    }

    protected applyDefaults(a: IAnimation) {
        return {
            duration: 1000,
            easing: eEasing.linear,
            iterations: 1,
            delay: 0,

            ...a,
        } as IAnimation;
    }

    protected valueIn(val: any) {
        if (val instanceof KbVector) {
            return val.toObject();
        } else if (val instanceof KbColor) {
            return val.toObject();
        } else if (val instanceof KbQuaternion) {
            return val.toObject();
        } else {
            return { value: val };
        }
    }

    protected valueOut(val: any, $type: string) {
        if (!$type) {
            return val.value;
        } else {
            if ($type == Token.KbVector.key) {
                return KbVector.From(val);
            } else if ($type == Token.KbColor.key) {
                return KbColor.From(val);
            } else if ($type == Token.KbQuaternion.key) {
                return KbQuaternion.From(val);
            } else {
                throw 'unrecognized type';
            }
        }
    }

    protected getTweenEasingFn(e: eEasing): (k: number) => number {
        const split = e.splitWords().map(s => s.capitalize());
        const inOut = split.length == 3 ? split[1] + split[2] : split.length == 2 ? split[1] : 'None';
        return (TWEEN.Easing as any)[split[0]][inOut];
    }
}
